import { useState, useEffect, useRef } from "react";
import { Mic, MicOff, Send, X, User } from "lucide-react";
import { useTranslation } from "react-i18next";
// import { useNavigate } from "react-router-dom"; // Not needed since we use window.location.href
import { Button } from "@/components/ui/button";
import { GroundingFiles } from "@/components/ui/grounding-files";
import GroundingFileView from "@/components/ui/grounding-file-view";
import StatusMessage from "@/components/ui/status-message";
import TopicList from "@/components/ui/topicList";
import TopicButton from "@/components/ui/topic-button";
import { getTextChatUrl } from "../config/api";
import { isSsoEnforced, TOPIC_NAV_SKIP_AUTH_PROFILE_KEY } from "@/config/sso";
import {
    CHAT_AUDIO_LISTEN_GRADIENT,
    CHAT_AUDIO_PAUSE,
    CHAT_AUDIO_PLAY,
    CHAT_DISABLED,
    CHAT_MUTED_CORAL,
    CHAT_PRIMARY,
    CHAT_PRIMARY_BG,
    CHAT_SURFACE,
    CHAT_TEXT,
    CHAT_TEXT_MUTED,
    APP_PAGE_BACKGROUND_STYLE,
    GRADIENT_TITLE_STYLE,
    SIDE_PANEL_CLASS,
    SIDE_PANEL_INNER_CLASS,
} from "@/lib/chatTheme";

import useRealTime from "@/hooks/useRealtime";
import useAudioRecorder from "@/hooks/useAudioRecorder";
import useAudioPlayer from "@/hooks/useAudioPlayer";
import { getStoredAccessToken, getUserDetails, getUserEmailFromProfile } from "@/services/ssoApi";

import { GroundingFile, ToolResult, ChatHistoryItem, TopicItem, type ThinkingBannerKind } from "../types";

import { TOPIC_ITEMS } from "@/lib/topicConfig";

import logo from "../assets/Beluga 1.png";
import "../App.css";

/** Text chat: retries when the server is unreachable or returns a transient error. */
const CHAT_FETCH_MAX_ATTEMPTS = 5;
/** Backend may run AILY + Solubara in parallel plus GPT merge — keep above worst-case wall time. */
const CHAT_FETCH_TIMEOUT_MS = 120_000;
const CHAT_FETCH_RETRY_BASE_MS = 600;

function chatRetryDelayMs(attemptIndex: number): number {
  return Math.min(10_000, CHAT_FETCH_RETRY_BASE_MS * 2 ** attemptIndex);
}

function isChatRetryableNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return true;
  if (error.name === "AbortError") return true;
  const m = error.message.toLowerCase();
  return (
    m.includes("fetch") ||
    m.includes("network") ||
    m.includes("failed to fetch") ||
    m.includes("load failed") ||
    m.includes("networkerror")
  );
}

function isChatTransientHttpStatus(status: number): boolean {
  return [429, 502, 503, 504].includes(status);
}

function ChatbotPage() {
    const [isRecording, setIsRecording] = useState(false);
    
    console.log('ChatbotPage rendered');
    const [isMicOff, setIsMicOff] = useState<Boolean>(false);
    const [groundingFiles, setGroundingFiles] = useState<GroundingFile[]>([]);
    const [selectedFile, setSelectedFile] = useState<GroundingFile | null>(null);
    const [isThinking, setIsThinking] = useState(false);
    const [thinkingBanner, setThinkingBanner] = useState<ThinkingBannerKind>("thinking");
    const [showTopicList, setShowTopicList] = useState(false);
    const [topicList, setTopicList] = useState<TopicItem[]>([]);
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [chatHistoryData, setChatHistoryData] = useState<ChatHistoryItem[]>([]);
    const [newChatHistory, setNewChatHistory] = useState('');
    const [inputText, setInputText] = useState('');
    const [isTextMode, setIsTextMode] = useState(true);
    const [sessionId] = useState(() => `text_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    const [ssoUserEmail, setSsoUserEmail] = useState<string | null>(null);
    const [ssoProfileReady, setSsoProfileReady] = useState(() => !isSsoEnforced());
    /** Abort prior text-chat fetch when user sends a new message (avoids out-of-order / stale answers). */
    const activeTextChatControllerRef = useRef<AbortController | null>(null);
    
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                if (!isSsoEnforced()) {
                    return;
                }
                const token = getStoredAccessToken();
                const details = await getUserDetails(token);
                if (cancelled) return;
                if (details && !("error" in details)) {
                    const em = getUserEmailFromProfile(details);
                    setSsoUserEmail(em ? em.trim().toLowerCase() : null);
                }
            } finally {
                if (!cancelled) {
                    setSsoProfileReady(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const list = TOPIC_ITEMS;

        const params = new URLSearchParams(window.location.search);
        const topic = params.get('topic');
        console.log('=== URL Topic Extraction Debug (Chatbot) ===');
        console.log('Current URL:', window.location.href);
        console.log('Search params:', window.location.search);
        console.log('Extracted topic:', topic);
        console.log('Topic type:', typeof topic);
        console.log('==========================================');
        
        // Normalize topic to lowercase underscore format
        let normalizedTopic = topic;
        if (topic) {
            // Convert common uppercase formats to lowercase underscore
            const topicMapping: { [key: string]: string } = {
                'FUTURE+MOBILITY': 'future_mobility',
                'FUTURE MOBILITY': 'future_mobility',
                'GREEN+LIVING': 'green_living',
                'GREEN LIVING': 'green_living',
                'SUSTAINABLE+PACKAGING': 'sustainable_packaging',
                'SUSTAINABLE PACKAGING': 'sustainable_packaging',
                'BETTER+CONSTRUCTION+AND+INFRASTRUCTURE': 'better_construction_and_infrastructure',
                'BETTER CONSTRUCTION AND INFRASTRUCTURE': 'better_construction_and_infrastructure',
                'RESOURCE+EFFICIENCY+FACTORY': 'resource_efficiency_factory',
                'RESOURCE EFFICIENCY FACTORY': 'resource_efficiency_factory',
                'HEALTH+&+WELL-BEING': 'health_well_being',
                'HEALTH & WELL-BEING': 'health_well_being',
                'CLOSING+THE+LOOP': 'closing_the_loop',
                'CLOSING THE LOOP': 'closing_the_loop',
                'NEED+ZONE': 'need_zone',
                'NEED ZONE': 'need_zone',
                'POLICY': 'policy',
                'SCGC+INFORMATION': 'scgc_info',
                'SCGC INFORMATION': 'scgc_info',
                'SCGC+INFO': 'scgc_info',
                'SCGC INFO': 'scgc_info',
            };
            
            normalizedTopic = topicMapping[topic] || topic.toLowerCase().replace(/[+ ]/g, '_');
            
            // If topic was normalized, update the URL
            if (normalizedTopic !== topic) {
                console.log('Normalizing topic from', topic, 'to', normalizedTopic);
                const newParams = new URLSearchParams(window.location.search);
                newParams.set('topic', normalizedTopic);
                const newUrl = `${window.location.pathname}?${newParams.toString()}`;
                window.history.replaceState({}, '', newUrl);
            }
        }
        
        setSelectedTopic(normalizedTopic)

        setTopicList(list)
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        }
    }, [])

    // Start WebSocket connection when page loads (for voice mode)
    useEffect(() => {
        console.log('=== Auto-starting WebSocket connection (Chatbot) ===');
        console.log('Chatbot page loaded, starting WebSocket session...');
        console.log('Starting with clean URL (no topic parameter)');
        console.log('==================================================');
        
        // Start WebSocket connection with clean URL (no topic parameter)
        startSessionBase(undefined);
        
        return () => {
            console.log('=== Cleaning up WebSocket connection (Chatbot) ===');
            console.log('Chatbot page unloaded, WebSocket will be closed');
            console.log('================================================');
        }
    }, []) // Empty dependency array means this runs once on mount

    // Auto scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistoryData, newChatHistory]);

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.code === 'Space' && !isTextMode) {
            event.preventDefault();
            setIsMicOff(!isMicOff)
        }
        if (event.key === 'Enter' && isTextMode && inputText.trim()) {
            event.preventDefault();
            handleSendMessage();
        }
    };

    const { startSession: startSessionBase, addUserAudio, inputAudioBufferClear, sendJsonMessage } = useRealTime({
        userEmail: ssoUserEmail,
        onWebSocketOpen: () => console.log("WebSocket connection opened"),
        onWebSocketClose: () => console.log("WebSocket connection closed"),
        onWebSocketError: event => console.error("WebSocket error:", event),
        onReceivedError: message => {
            console.error("error", message);
            setIsThinking(false);
            setThinkingBanner("thinking");
        },
        onReceivedResponseAudioDelta: async message => {
            if (message.delta && message.delta.length > 0) {
                setIsThinking(false);
                setThinkingBanner("thinking");
            }

            void playAudio(message.delta);
        },
        onReceivedInputAudioBufferSpeechStarted: () => {
            stopAudioPlayer();
        },
        onReceivedExtensionMiddleTierToolResponse: message => {            
            const result: ToolResult = JSON.parse(message.tool_result);

            const files: GroundingFile[] = result.sources.map(x => {
                return { id: '0', name: x.fileName, content: x.text };
            });

            setGroundingFiles(prev => [...prev, ...files]);
        },
        setIsThinking,
        setThinkingBanner,
        setTextChatHistory: (id: string, text: string , type: string, replace = false) => {
            const tempChatHistoryData: ChatHistoryItem[] = chatHistoryData
            const indexItem = tempChatHistoryData.findIndex(item=> item.id === id)
            if (indexItem === -1) {
                const now = new Date();
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const time = `${hours}:${minutes}`;

                tempChatHistoryData.push({id, text, type, time})
                setChatHistoryData(tempChatHistoryData)
            } else {
                tempChatHistoryData[indexItem].text = replace ? text : (tempChatHistoryData[indexItem].text + text)
                setChatHistoryData(tempChatHistoryData)
            }
            setNewChatHistory(text)
        }
    });

    const { reset: resetAudioPlayer, play: playAudio, stop: stopAudioPlayer } = useAudioPlayer();
    const { start: startAudioRecording, stop: stopAudioRecording } = useAudioRecorder({ onAudioRecorded: addUserAudio });

    // Wrapper function to start session with topic
    const startSession = async () => {
        console.log('=== startSession Debug (Chatbot) ===');
        console.log('selectedTopic state:', selectedTopic);
        console.log('selectedTopic type:', typeof selectedTopic);
        console.log('Passing to startSessionBase:', selectedTopic || undefined);
        console.log('==================================');
        await startSessionBase(selectedTopic || undefined);
    };

    const onToggleListening = async () => {
        if (!isRecording) {
            startSession();
            await startAudioRecording();
            await resetAudioPlayer();
            if (isMicOff) {
                await stopAudioRecording();
            }
            setIsRecording(true);
        } else {
            await stopAudioRecording();
            stopAudioPlayer();
            inputAudioBufferClear();
            setIsRecording(false);
        }
    };

    const handelSetMicOff = async () => {
        setIsThinking(false)
        setThinkingBanner("thinking")
        if (isMicOff) {
            setIsMicOff(false)
            await startAudioRecording();
            console.log('turn on microphone');
        } else {
            setIsMicOff(true)
            await stopAudioRecording();
            console.log('turn off microphone');
        }
    }

    const handleSendMessage = async () => {
        if (isSsoEnforced() && !ssoProfileReady) {
            return;
        }
        if (inputText.trim()) {
            // Add user message to chat history
            const now = new Date();
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const time = `${hours}:${minutes}`;
            
            const userMessage: ChatHistoryItem = {
                id: `user_${Date.now()}`,
                text: inputText,
                type: 'input',
                time: time
            };
            
            setChatHistoryData(prev => [...prev, userMessage]);
            const messageText = inputText;
            setInputText('');
            
            // Show thinking indicator
            setIsThinking(true);
            setThinkingBanner("thinking");

            activeTextChatControllerRef.current?.abort();
            const fetchAbortController = new AbortController();
            activeTextChatControllerRef.current = fetchAbortController;

            const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

            let textChatSuperseded = false;

            try {
                for (let attempt = 0; attempt < CHAT_FETCH_MAX_ATTEMPTS; attempt++) {
                    try {
                        const controller = fetchAbortController;
                        const timeoutId = setTimeout(() => controller.abort(), CHAT_FETCH_TIMEOUT_MS);
                        try {
                        const response = await fetch(getTextChatUrl(), {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                text: messageText,
                                session_id: sessionId,
                                topic: selectedTopic,
                                ...(ssoUserEmail ? { user_email: ssoUserEmail } : {}),
                            }),
                            signal: controller.signal,
                        });

                        if (response.ok) {
                            const data = await response.json();
                            console.log("=== Chat Response Debug ===");
                            console.log("Full response data:", data);
                            console.log("Response structure check:");
                            console.log("- data.data?.content?.[0]?.text:", data.data?.content?.[0]?.text);
                            console.log("- data.agent_response:", data.agent_response);
                            console.log("- data.response:", data.response);
                            console.log("- data.message:", data.message);
                            console.log("- data.text:", data.text);
                            console.log("==========================");

                            let botResponse = "No response received";
                            if (data.data && data.data.content && data.data.content[0] && data.data.content[0].text) {
                                botResponse = data.data.content[0].text;
                            } else if (data.agent_response) {
                                botResponse = data.agent_response;
                            } else if (data.response) {
                                botResponse = data.response;
                            } else if (data.message) {
                                botResponse = data.message;
                            } else if (data.text) {
                                botResponse = data.text;
                            }

                            const botMessage: ChatHistoryItem = {
                                id: `bot_${Date.now()}`,
                                text: botResponse,
                                type: "output",
                                time: time,
                            };
                            setChatHistoryData((prev) => [...prev, botMessage]);
                            if (activeTextChatControllerRef.current === fetchAbortController) {
                                activeTextChatControllerRef.current = null;
                            }
                            break;
                        }

                        if (
                            isChatTransientHttpStatus(response.status) &&
                            attempt < CHAT_FETCH_MAX_ATTEMPTS - 1
                        ) {
                            console.warn(
                                `Text chat HTTP ${response.status}; retry ${attempt + 1}/${CHAT_FETCH_MAX_ATTEMPTS}`,
                            );
                            await sleep(chatRetryDelayMs(attempt));
                            continue;
                        }

                        let errLabel = "Failed to get response";
                        try {
                            const errorData = await response.json();
                            errLabel = (errorData as { error?: string }).error || errLabel;
                        } catch {
                            /* ignore non-JSON error body */
                        }
                        const errorMessage: ChatHistoryItem = {
                            id: `bot_${Date.now()}`,
                            text: `Error: ${errLabel}`,
                            type: "output",
                            time: time,
                        };
                        setChatHistoryData((prev) => [...prev, errorMessage]);
                        if (activeTextChatControllerRef.current === fetchAbortController) {
                            activeTextChatControllerRef.current = null;
                        }
                        break;
                        } finally {
                            clearTimeout(timeoutId);
                        }
                    } catch (error) {
                        const isAbort =
                            error instanceof DOMException
                                ? error.name === "AbortError"
                                : error instanceof Error && error.name === "AbortError";
                        if (isAbort && activeTextChatControllerRef.current !== fetchAbortController) {
                            textChatSuperseded = true;
                            return;
                        }
                        const canRetry =
                            isChatRetryableNetworkError(error) && attempt < CHAT_FETCH_MAX_ATTEMPTS - 1;
                        if (canRetry) {
                            console.warn(
                                `Text chat request failed (attempt ${attempt + 1}/${CHAT_FETCH_MAX_ATTEMPTS}):`,
                                error,
                            );
                            await sleep(chatRetryDelayMs(attempt));
                            continue;
                        }
                        console.error("Error sending message:", error);
                        const failText = isAbort
                            ? "Request timed out waiting for the server. Please try again."
                            : "Fail to connect to server please try again";
                        const errorMessage: ChatHistoryItem = {
                            id: `bot_${Date.now()}`,
                            text: failText,
                            type: "output",
                            time: time,
                        };
                        setChatHistoryData((prev) => [...prev, errorMessage]);
                        if (activeTextChatControllerRef.current === fetchAbortController) {
                            activeTextChatControllerRef.current = null;
                        }
                        break;
                    }
                }
            } finally {
                if (!textChatSuperseded) {
                    setIsThinking(false);
                    setThinkingBanner("thinking");
                }
            }
        }
    };

    const toggleMode = () => {
        setIsTextMode(!isTextMode);
        if (isRecording) {
            onToggleListening();
        }
    };

    const handleClickShowTopicList = async () => {
        setShowTopicList(!showTopicList)
    }
    
    const handleClickSelectTopic = async (key: string) => {
        const normalizedKey = key.toLowerCase().replace(/[+ ]/g, "_");
        if (isSsoEnforced()) {
            sessionStorage.setItem(TOPIC_NAV_SKIP_AUTH_PROFILE_KEY, "1");
        }
        const params = new URLSearchParams(window.location.search);
        params.set("topic", normalizedKey);
        const qs = params.toString();
        window.location.assign(qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    };

    const handleClickCancelSelectTopic = async () => { 
        console.log('=== Topic Cancellation Debug (Chatbot) ===');
        console.log('Cancelling topic selection');
        console.log('WebSocket available:', !!sendJsonMessage);
        console.log('========================================');
        
        // Update URL
        const params = new URLSearchParams(window.location.search);
        params.delete('topic');
        window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
        
        // Update state
        setSelectedTopic(null);
        
        // Send topic removal to backend immediately via WebSocket
        if (sendJsonMessage) {
            try {
                const topicMessage = {
                    type: "session.update",
                    session: {
                        type: "realtime" as const,
                        topic: null
                    }
                };
                console.log('Sending topic removal to backend (Chatbot):', topicMessage);
                sendJsonMessage(topicMessage);
            } catch (error) {
                console.error('Failed to send topic removal to backend (Chatbot):', error);
            }
        } else {
            console.warn('WebSocket not available, topic removal will be sent when microphone is opened');
        }
    }

    const { t } = useTranslation();

    return (
        <div
            className="relative flex min-h-screen min-h-[100dvh] w-full items-stretch"
            style={APP_PAGE_BACKGROUND_STYLE}
        >

            { 
                showTopicList && (
                    <X
                        onClick={handleClickShowTopicList}
                        className="fixed right-[max(1rem,env(safe-area-inset-right))] top-[max(0.75rem,env(safe-area-inset-top))] z-[120] h-9 w-9 cursor-pointer rounded-full bg-white/90 p-2 shadow-md sm:absolute sm:right-6 sm:top-6 sm:h-5 sm:w-5 sm:bg-transparent sm:p-0 sm:shadow-none"
                        style={{
                            color: '#1A1A1A',
                        }}
                        aria-label="Close panel"
                    />
                ) 
            }

            <div 
                className="flex max-h-[100dvh] min-h-0 min-w-0 flex-1 flex-col overflow-hidden text-gray-900"
                style={{
                    position: 'relative',
                }}
            > 
                {/* Desktop / tablet: topic + history — absolute so main content stays centered */}
                <div
                    className={`${
                        showTopicList ? 'hidden md:flex' : 'hidden sm:flex'
                    } z-[100] max-w-[min(92vw,28rem)] items-center`}
                    style={{
                        position: 'absolute',
                        top: 20,
                        right: 30,
                    }}
                >
                    <TopicButton
                        selectedTopic={selectedTopic}
                        topicList={topicList}
                        onOpen={handleClickShowTopicList}
                        onClear={handleClickCancelSelectTopic}
                        className="max-w-[min(70vw,20rem)]"
                    />
                </div>

                {/* Main Content */}
                <main className="flex min-h-0 flex-1 flex-col items-stretch overflow-hidden px-3 max-md:px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-[max(0.35rem,env(safe-area-inset-top))] sm:min-h-0 sm:items-center sm:overflow-visible sm:px-0 sm:pb-0 sm:pt-0">
                    {/* Mobile: top bar (logo + topic + history) — in document flow to avoid overlap */}
                    <div
                        className={`mb-2 flex w-full max-w-[800px] shrink-0 items-center gap-2 max-md:ps-11 sm:hidden ${
                            showTopicList ? 'hidden' : ''
                        }`}
                    >
                        <img src={logo} alt="" className="h-10 w-auto shrink-0 object-contain" aria-hidden />
                        <div className="min-w-0 flex-1" />
                        <TopicButton
                            selectedTopic={selectedTopic}
                            topicList={topicList}
                            onOpen={handleClickShowTopicList}
                            onClear={handleClickCancelSelectTopic}
                            className="max-w-[45vw]"
                        />
                    </div>

                    {/* Logo and Title */}
                    <div
                        className={`flex w-full max-w-[800px] shrink-0 items-center justify-center gap-3 ${
                            showTopicList ? 'mb-1 mt-0 sm:mt-[3vh]' : 'mb-3 mt-1 sm:mt-[3vh]'
                        }`}
                    >
                        <img src={logo} alt="Channel logo" className="hidden h-[4em] object-contain sm:block md:h-[6em] lg:h-[7em]" />
                        <h1 
                            className={`min-w-0 shrink text-center font-bold leading-tight sm:mt-1.5 sm:text-[1.75em] md:text-[2.25em] lg:text-[2.75em] ${
                                showTopicList ? 'text-lg' : ''
                            }`}
                            style={GRADIENT_TITLE_STYLE}
                        >
                            {t("app.title")} Chat
                        </h1>
                    </div>

                    {/* Chat Interface */}
                    <div 
                        className={`${showTopicList ? 'hidden sm:flex' : 'flex'} min-h-0 min-w-0 w-full max-w-[800px] flex-1 flex-col overflow-hidden px-0 sm:overflow-visible sm:px-0`}
                        style={{
                            position: 'relative',
                            justifyContent: 'flex-start',
                            alignItems: 'stretch',
                            margin: '12px 0 8px',
                            paddingBottom: '4px',
                        }}
                    >
                        {/* Chat Messages */}
                        <div
                            className="mb-3 flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-3 shadow-md sm:mb-4 sm:p-4 max-md:max-h-[min(72dvh,calc(100dvh-12rem))] max-md:min-h-[6rem] sm:max-h-[min(520px,calc(100dvh-14rem))] sm:min-h-0"
                            style={{ boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)" }}
                        >
                            <div
                                className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain rounded-lg border border-gray-100 p-3 [scrollbar-gutter:stable] sm:p-4"
                                style={{ backgroundColor: CHAT_SURFACE }}
                            >
                                {chatHistoryData.length === 0 ? (
                                    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-2 py-10 text-center">
                                        <img
                                            src={logo}
                                            alt="Beluga"
                                            className="mb-4 h-20 w-20 object-contain sm:h-24 sm:w-24"
                                        />
                                        <p className="text-base font-medium" style={{ color: CHAT_TEXT }}>
                                            Start a conversation with Beluga!
                                        </p>
                                        <p className="mt-1 text-sm" style={{ color: CHAT_TEXT_MUTED }}>
                                            Ask anything about SCGC topics
                                        </p>
                                    </div>
                                ) : (
                                    <div className="flex min-h-0 min-w-0 flex-col gap-4">
                                        {chatHistoryData.map((message) =>
                                            message.type === "input" ? (
                                                <div
                                                    key={message.id}
                                                    className="flex min-w-0 w-full items-start justify-end gap-2"
                                                >
                                                    <div className="flex max-w-[min(100%,36rem)] min-w-0 flex-col items-end">
                                                        <div
                                                            className="min-w-0 shrink rounded-xl px-4 py-2.5 shadow-sm"
                                                            style={{
                                                                backgroundColor: CHAT_PRIMARY,
                                                                color: "white",
                                                            }}
                                                        >
                                                            <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                                                                {message.text}
                                                            </p>
                                                        </div>
                                                        <p
                                                            className="mt-1 text-xs"
                                                            style={{ color: CHAT_TEXT_MUTED }}
                                                        >
                                                            {message.time}
                                                        </p>
                                                    </div>
                                                    <div
                                                        className="flex h-8 w-8 shrink-0 items-center justify-center self-start rounded-full"
                                                        style={{ backgroundColor: CHAT_PRIMARY_BG }}
                                                        aria-hidden
                                                    >
                                                        <User className="h-4 w-4" style={{ color: CHAT_PRIMARY }} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div
                                                    key={message.id}
                                                    className="flex min-w-0 w-full items-start gap-2"
                                                >
                                                    <img
                                                        src={logo}
                                                        alt=""
                                                        className="h-8 w-8 shrink-0 self-start rounded-full object-contain"
                                                        aria-hidden
                                                    />
                                                    <div className="flex max-w-[min(100%,36rem)] min-w-0 flex-col">
                                                        <div
                                                            className="min-w-0 shrink rounded-xl px-4 py-2.5 shadow-sm"
                                                            style={{
                                                                backgroundColor: CHAT_PRIMARY_BG,
                                                                color: CHAT_TEXT,
                                                            }}
                                                        >
                                                            <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                                                                {message.text}
                                                            </p>
                                                        </div>
                                                        <p
                                                            className="mt-1 text-xs"
                                                            style={{ color: CHAT_TEXT_MUTED }}
                                                        >
                                                            {message.time}
                                                        </p>
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                        {isThinking && (
                                            <div className="flex min-w-0 w-full items-start gap-2">
                                                <img
                                                    src={logo}
                                                    alt=""
                                                    className="h-8 w-8 shrink-0 self-start rounded-full object-contain"
                                                    aria-hidden
                                                />
                                                <div
                                                    className="max-w-[min(100%,36rem)] min-w-0 shrink rounded-xl px-4 py-2.5 shadow-sm"
                                                    style={{
                                                        backgroundColor: CHAT_PRIMARY_BG,
                                                        color: CHAT_TEXT,
                                                    }}
                                                >
                                                    <p className="text-sm">
                                                        {thinkingBanner === "searching"
                                                            ? "Searching..."
                                                            : "Thinking..."}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Input Area */}
                        <div
                            className="flex min-w-0 w-full shrink-0 items-center gap-1.5 rounded-[12px] border border-gray-200/90 bg-white px-2 py-1.5 sm:gap-2 sm:px-2.5 sm:py-2"
                            style={{ boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)" }}
                        >
                            <Button
                                onClick={toggleMode}
                                className="h-8 shrink-0 rounded-lg px-3 text-sm font-medium hover:opacity-90 sm:h-9 sm:px-3.5"
                                style={{
                                    backgroundColor: CHAT_PRIMARY,
                                    color: "white",
                                    border: "none",
                                    cursor: "pointer",
                                }}
                            >
                                {isTextMode ? "Text" : "Voice"}
                            </Button>

                            {isTextMode ? (
                                <>
                                    <input
                                        type="text"
                                        value={inputText}
                                        onChange={(e) => setInputText(e.target.value)}
                                        placeholder={
                                            isSsoEnforced() && !ssoProfileReady
                                                ? "Loading profile…"
                                                : "Type your message..."
                                        }
                                        className="min-w-0 flex-1 border-0 bg-transparent px-1 py-1.5 text-base text-[#1A1A1A] placeholder:text-[#888888] focus:outline-none focus:ring-0 sm:px-2"
                                        onKeyPress={(e) =>
                                            e.key === "Enter" &&
                                            (!isSsoEnforced() || ssoProfileReady) &&
                                            handleSendMessage()
                                        }
                                    />
                                    <Button
                                        onClick={handleSendMessage}
                                        disabled={
                                            !inputText.trim() ||
                                            (isSsoEnforced() && !ssoProfileReady)
                                        }
                                        className="h-8 w-8 shrink-0 rounded-lg p-0 hover:opacity-90 sm:h-9 sm:w-9"
                                        style={{
                                            backgroundColor:
                                                inputText.trim() &&
                                                (!isSsoEnforced() || ssoProfileReady)
                                                    ? CHAT_PRIMARY
                                                    : CHAT_DISABLED,
                                            color:
                                                inputText.trim() &&
                                                (!isSsoEnforced() || ssoProfileReady)
                                                    ? "white"
                                                    : CHAT_TEXT_MUTED,
                                            border: "none",
                                            cursor:
                                                inputText.trim() &&
                                                (!isSsoEnforced() || ssoProfileReady)
                                                    ? "pointer"
                                                    : "not-allowed",
                                        }}
                                    >
                                        <Send className="h-4 w-4" />
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        onClick={onToggleListening}
                                        className="min-w-0 h-8 flex-1 rounded-lg px-2 text-sm font-medium hover:opacity-90 sm:h-9 sm:px-4 sm:text-base"
                                        style={{
                                            background: isRecording ? CHAT_AUDIO_PAUSE : CHAT_AUDIO_PLAY,
                                            color: "white",
                                            border: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {isRecording ? (
                                            <>
                                                <MicOff className="mr-2 h-4 w-4" />
                                                Stop Recording
                                            </>
                                        ) : (
                                            <>
                                                <Mic className="mr-2 h-4 w-4" />
                                                Start Recording
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        onClick={handelSetMicOff}
                                        className="h-8 w-8 shrink-0 rounded-lg p-0 hover:opacity-90 sm:h-9 sm:w-9"
                                        aria-label={isMicOff ? "Unmute microphone" : "Mute microphone"}
                                        style={{
                                            background: isMicOff ? CHAT_MUTED_CORAL : CHAT_AUDIO_LISTEN_GRADIENT,
                                            transition: "background 0.3s ease",
                                            border: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        {isMicOff ? (
                                            <MicOff className="h-4 w-4" style={{ color: "white" }} />
                                        ) : (
                                            <Mic className="h-4 w-4" style={{ color: "white" }} />
                                        )}
                                    </Button>
                                </>
                            )}
                        </div>

                        {/* Status Message */}
                        <div className="mt-2 shrink-0 sm:mt-[10px]">
                            {isRecording && <StatusMessage isRecording={isRecording} />}
                            <GroundingFiles files={groundingFiles} onSelected={setSelectedFile} />
                        </div>
                    </div>

                    {/* Mobile Topic List */}
                    <div
                        className={`${showTopicList ? 'flex sm:hidden' : 'hidden'} min-h-0 min-w-0 max-h-[min(92dvh,100dvh)] w-full max-w-[800px] flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-md`}
                        style={{
                            maxHeight: 'calc(100dvh - max(5rem, env(safe-area-inset-top)) - max(3rem, env(safe-area-inset-bottom)))',
                            margin: '8px 0 4px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        {
                            showTopicList && (
                                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                                    <h4
                                        className="mb-2 shrink-0 text-xl font-bold"
                                        style={GRADIENT_TITLE_STYLE}
                                    >
                                        Topic
                                    </h4>
                                    <p className="mb-3 shrink-0 text-sm text-gray-500">
                                        Selecting a topic helps improve AI accuracy.
                                    </p>
                                    <div className={SIDE_PANEL_INNER_CLASS}>
                                        <TopicList
                                            selectedTopic={selectedTopic}
                                            topicList={topicList}
                                            handleClickSelectTopic={handleClickSelectTopic}
                                            scrollHeight="370px"
                                        />
                                    </div>
                                </div>
                            )
                        }
                    </div>
                </main>

                <footer className="shrink-0 px-3 py-3 text-right text-[12px] sm:mr-5 sm:py-4" style={{color: 'rgba(0, 0, 0, 0.5)'}}>
                    {t("app.footer")}
                </footer>

                <GroundingFileView groundingFile={selectedFile} onClosed={() => setSelectedFile(null)} />
            </div>

            {/* Desktop Topic List Panel */}
            {
                showTopicList && (
                    <div
                        className={SIDE_PANEL_CLASS}
                        style={{
                            margin: '15px 15px 15px 0px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <h4
                            className="shrink-0 text-2xl font-bold md:text-3xl"
                            style={{
                                ...GRADIENT_TITLE_STYLE,
                                marginBottom: '10px',
                            }}
                        >
                            Topic
                        </h4>
                        <div className="shrink-0 text-[14px] text-gray-500" style={{ marginBottom: '12px' }}>
                            Selecting a topic helps improve AI accuracy.
                        </div>
                        <div className={SIDE_PANEL_INNER_CLASS}>
                            <TopicList
                                selectedTopic={selectedTopic}
                                topicList={topicList}
                                handleClickSelectTopic={handleClickSelectTopic}
                                scrollHeight="200px"
                            />
                        </div>
                    </div>
                )
            }
            
        </div>
    );
}

export default ChatbotPage;
